import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { connectApi, apiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Video, AlertTriangle } from "lucide-react";

const ZOOM_VERSION = "6.2.0";
const CDN = `https://source.zoom.us/${ZOOM_VERSION}`;
const SCRIPTS = [
  `${CDN}/lib/vendor/react.min.js`,
  `${CDN}/lib/vendor/react-dom.min.js`,
  `${CDN}/lib/vendor/redux.min.js`,
  `${CDN}/lib/vendor/redux-thunk.min.js`,
  `${CDN}/lib/vendor/lodash.min.js`,
  `${CDN}/zoom-meeting-${ZOOM_VERSION}.min.js`,
];

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src; s.async = false;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
    document.body.appendChild(s);
  });
}

async function loadZoom() {
  if (window.ZoomMtg) return window.ZoomMtg;
  for (const src of SCRIPTS) await loadScript(src); // secuencial (dependencias)
  return window.ZoomMtg;
}

function setRootVisible(visible) {
  const el = document.getElementById("zmmtg-root");
  if (el) el.style.display = visible ? "block" : "none";
}

export default function SuperaConnectRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const uidRef = useRef(Math.floor(Math.random() * 1_000_000_00) + 1);
  const hbRef = useRef(null);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | joining | in | error
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await connectApi.get(sessionId);
        if (!alive) return;
        setSession(s);
        if (!s.configured) { setStatus("error"); setErrMsg("Supera Connect (Zoom) aún no está configurado."); return; }
        if (!s.has_meeting) { setStatus("error"); setErrMsg("El anfitrión aún no ha configurado el ID de reunión de Zoom para esta sesión."); return; }
        setStatus("ready");
      } catch (e) {
        if (!alive) return;
        setStatus("error"); setErrMsg(apiError(e, "No se pudo cargar la sesión."));
      }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  useEffect(() => () => {
    if (hbRef.current) clearInterval(hbRef.current);
    setRootVisible(false);
    try { window.ZoomMtg?.leaveMeeting?.({}); } catch {}
    connectApi.leave(sessionId, uidRef.current).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const join = async () => {
    setStatus("joining");
    try {
      try { await connectApi.join(sessionId, uidRef.current); } catch {}
      hbRef.current = setInterval(() => { connectApi.heartbeat(sessionId, uidRef.current).catch(() => {}); }, 25000);

      const data = await connectApi.joinToken(sessionId);
      const ZoomMtg = await loadZoom();
      if (!ZoomMtg) throw new Error("No se pudo cargar el SDK de Zoom.");

      ZoomMtg.setZoomJSLib(`${CDN}/lib`, "/av");
      ZoomMtg.preLoadWasm();
      ZoomMtg.prepareWebSDK();
      setRootVisible(true);

      ZoomMtg.init({
        leaveUrl: `${window.location.origin}/en-vivo`,
        patchJsMedia: true,
        disablePreview: false,
        success: () => {
          ZoomMtg.join({
            sdkKey: data.sdkKey,
            signature: data.signature,
            meetingNumber: data.meetingNumber,
            passWord: data.password || "",
            userName: data.userName,
            success: () => setStatus("in"),
            error: (err) => {
              setRootVisible(false);
              const m = err?.reason || err?.errorMessage || "No se pudo entrar a la reunión de Zoom.";
              setErrMsg(m); setStatus("ready"); toast.error(m);
            },
          });
        },
        error: (err) => {
          setRootVisible(false);
          const m = err?.reason || err?.errorMessage || "No se pudo iniciar Zoom.";
          setErrMsg(m); setStatus("ready"); toast.error(m);
        },
      });
    } catch (e) {
      if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
      const msg = apiError(e, e?.message || "No se pudo entrar a la reunión de Zoom.");
      setErrMsg(msg); setStatus("ready"); toast.error(msg);
    }
  };

  const goBack = async () => {
    if (hbRef.current) clearInterval(hbRef.current);
    setRootVisible(false);
    try { await connectApi.leave(sessionId, uidRef.current); } catch {}
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-[#0A1420] text-white flex flex-col" data-testid="connect-room">
      <header className="bg-[#0A1C30] border-b border-white/10 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={goBack} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold" data-testid="connect-room-back">
            <ArrowLeft className="w-4 h-4" /> Salir
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <Video className="w-4 h-4 text-[#C0140F] flex-shrink-0" />
            <span className="font-extrabold truncate">{session?.titulo || "Supera Connect"}</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {status === "loading" && <Loader2 className="w-8 h-8 animate-spin text-white/60" data-testid="connect-room-loading" />}

        {status === "error" && (
          <div className="text-center max-w-md" data-testid="connect-room-error">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-400" />
            <p className="font-bold text-lg">No es posible entrar todavía</p>
            <p className="text-white/60 text-sm mt-1">{errMsg}</p>
            <button onClick={goBack} className="mt-5 rounded-xl bg-white/10 hover:bg-white/20 px-5 py-2.5 font-bold text-sm">Volver</button>
          </div>
        )}

        {(status === "ready" || status === "joining") && (
          <div className="text-center" data-testid="connect-room-lobby">
            <div className="w-16 h-16 rounded-2xl bg-[#C0140F] grid place-items-center mx-auto mb-4"><Video className="w-8 h-8 text-white" /></div>
            <p className="font-extrabold text-xl">{session?.titulo}</p>
            {session?.descripcion && <p className="text-white/60 text-sm mt-1 max-w-md">{session.descripcion}</p>}
            <p className="text-white/50 text-xs mt-3 max-w-sm mx-auto">La videollamada se abre aquí mismo. Si el anfitrión aún no inicia la reunión en Zoom, verás la sala de espera.</p>
            {errMsg && <p className="text-amber-400 text-sm mt-3">{errMsg}</p>}
            <button onClick={join} disabled={status === "joining"} data-testid="connect-room-join"
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#C0140F] hover:bg-[#a01109] disabled:opacity-60 px-6 py-3 font-extrabold">
              {status === "joining" ? <><Loader2 className="w-5 h-5 animate-spin" /> Conectando…</> : <><Video className="w-5 h-5" /> Entrar a la videollamada</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
